import type {
  UTMParameters,
  LeadSourceData,
  AnalyticsEvent,
} from "../types/analytics.types";

// Get analytics IDs from environment
const GA4_MEASUREMENT_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID || "";
const FACEBOOK_PIXEL_ID = import.meta.env.VITE_FACEBOOK_PIXEL_ID || "";

/**
 * Extract UTM parameters from URL
 */
export function getUTMParameters(): UTMParameters {
  const urlParams = new URLSearchParams(window.location.search);

  return {
    utm_source: urlParams.get("utm_source") || undefined,
    utm_medium: urlParams.get("utm_medium") || undefined,
    utm_campaign: urlParams.get("utm_campaign") || undefined,
    utm_content: urlParams.get("utm_content") || undefined,
    utm_term: urlParams.get("utm_term") || undefined,
  };
}

/**
 * Determine traffic source from referrer and UTM params
 */
export function determineTrafficSource(
  referrer: string,
  utmParams: UTMParameters,
): string {
  // Check UTM source first (most reliable)
  if (utmParams.utm_source) {
    const source = utmParams.utm_source.toLowerCase();

    if (source.includes("facebook") || source.includes("fb")) return "Facebook";
    if (source.includes("instagram") || source.includes("ig"))
      return "Instagram";
    if (source.includes("google")) {
      if (utmParams.utm_medium === "cpc" || utmParams.utm_medium === "paid") {
        return "Google Ads";
      }
      return "Google";
    }
    if (source.includes("twitter") || source.includes("x.com"))
      return "Twitter";
    if (source.includes("linkedin")) return "LinkedIn";
    if (source.includes("tiktok")) return "TikTok";
    if (source.includes("youtube")) return "YouTube";

    // Return capitalized UTM source
    return (
      utmParams.utm_source.charAt(0).toUpperCase() +
      utmParams.utm_source.slice(1)
    );
  }

  // Fallback to referrer analysis
  if (referrer) {
    const ref = referrer.toLowerCase();

    if (ref.includes("facebook.com") || ref.includes("fb.com"))
      return "Facebook";
    if (ref.includes("instagram.com")) return "Instagram";
    if (ref.includes("google.com") || ref.includes("google."))
      return "Google Organic";
    if (ref.includes("bing.com")) return "Bing";
    if (ref.includes("yahoo.com")) return "Yahoo";
    if (ref.includes("twitter.com") || ref.includes("t.co")) return "Twitter";
    if (ref.includes("linkedin.com")) return "LinkedIn";
    if (ref.includes("tiktok.com")) return "TikTok";
    if (ref.includes("youtube.com")) return "YouTube";

    return "Referral";
  }

  return "Direct";
}

/**
 * Get complete lead source data
 */
export function getLeadSourceData(): LeadSourceData {
  const utmParams = getUTMParameters();
  const referrer = document.referrer || "";
  const landingPage = window.location.href;
  const source = determineTrafficSource(referrer, utmParams);

  // Store in sessionStorage for persistence during session
  const sessionData = {
    source,
    referrer,
    landingPage,
    utmParams,
    timestamp: new Date().toISOString(),
  };

  // Store original landing data if not already stored
  if (!sessionStorage.getItem("leadSourceData")) {
    sessionStorage.setItem("leadSourceData", JSON.stringify(sessionData));
  }

  return sessionData;
}

/**
 * Get stored lead source data (persists through session)
 */
export function getStoredLeadSourceData(): LeadSourceData | null {
  const stored = sessionStorage.getItem("leadSourceData");
  if (stored) {
    try {
      return JSON.parse(stored) as LeadSourceData;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Initialize Google Analytics 4
 */
export function initGA4(): void {
  if (!GA4_MEASUREMENT_ID) {
    return;
  }

  // Load GA4 script
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments);
  };

  // @ts-ignore - gtag accepts Date objects
  window.gtag("js", new Date());
  window.gtag("config", GA4_MEASUREMENT_ID, {
    send_page_view: true,
  });
}

/**
 * Initialize Facebook Pixel
 */
export function initFacebookPixel(): void {
  if (!FACEBOOK_PIXEL_ID) {
    return;
  }

  // Facebook Pixel Code
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (function (f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(
    window,
    document,
    "script",
    "https://connect.facebook.net/en_US/fbevents.js",
  );

  window.fbq?.("init", FACEBOOK_PIXEL_ID);
  window.fbq?.("track", "PageView");
}

/**
 * Track custom event in GA4
 */
export function trackGA4Event(event: AnalyticsEvent): void {
  if (!GA4_MEASUREMENT_ID || !window.gtag) return;

  window.gtag("event", event.eventName, event.eventParams);
}

/**
 * Track custom event in Facebook Pixel
 */
export function trackFacebookEvent(
  eventName: string,
  params?: Record<string, unknown>,
): void {
  if (!FACEBOOK_PIXEL_ID || !window.fbq) return;

  window.fbq("track", eventName, params);
}

/**
 * Track lead conversion across all platforms
 */
export function trackLeadConversion(packageName?: string): void {
  const leadSource = getStoredLeadSourceData() || getLeadSourceData();

  // Track in GA4
  trackGA4Event({
    eventName: "generate_lead",
    eventParams: {
      package: packageName || "None",
      source: leadSource.source,
      utm_source: leadSource.utmParams.utm_source || "",
      utm_medium: leadSource.utmParams.utm_medium || "",
      utm_campaign: leadSource.utmParams.utm_campaign || "",
    },
  });

  // Track in Facebook Pixel
  trackFacebookEvent("Lead", {
    content_name: packageName || "Quote Request",
    source: leadSource.source,
    value: 0,
    currency: "AUD",
  });
}

/**
 * Track button click
 */
export function trackButtonClick(
  buttonName: string,
  packageName?: string,
): void {
  trackGA4Event({
    eventName: "button_click",
    eventParams: {
      button_name: buttonName,
      package: packageName || "None",
    },
  });
}

/**
 * Track modal open
 */
export function trackModalOpen(modalName: string, packageName?: string): void {
  trackGA4Event({
    eventName: "modal_open",
    eventParams: {
      modal_name: modalName,
      package: packageName || "None",
    },
  });
}

/**
 * Initialize all analytics
 */
export function initAnalytics(): void {
  // Capture lead source data immediately
  getLeadSourceData();

  // Initialize tracking platforms
  initGA4();
  initFacebookPixel();
}
